import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Balance,
  ArrowBack,
  SportsSoccer,
  Scoreboard,
  Style,
} from "@mui/icons-material";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, Container } from "../design-system/components";
import usePageTitle from "../hooks/usePageTitle";
import {
  getCompeticionesArbitros,
  getEstadisticasArbitro,
} from "../services/arbitroEstadisticasService";
import {
  aggregateRefereeStatistics,
  average,
  normalizeRefereeCompetitions,
  percentage,
  refereeDisplayName,
  safeCount,
} from "../helpers/arbitroEstadisticasHelper";

const useNumberFormat = () => {
  const { i18n } = useTranslation("referees");
  return useMemo(
    () => new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { maximumFractionDigits: 2 }),
    [i18n.resolvedLanguage, i18n.language]
  );
};

const MetricCard = ({ icon: Icon, label, value, detail, color }) => {
  const theme = useTheme();
  return (
    <Card sx={{ height: "100%", border: `1px solid ${theme.palette.divider}` }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(color, 0.14),
              color,
              flexShrink: 0,
            }}
          >
            <Icon aria-hidden="true" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography
              component="p"
              variant="h5"
              fontWeight={800}
              color="text.primary"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {value}
            </Typography>
            {detail && (
              <Typography variant="caption" color="text.secondary">
                {detail}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const DistributionRow = ({ label, value, total, color }) => {
  const numFmt = useNumberFormat();
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" spacing={2} mb={0.75}>
        <Typography variant="body2" color="text.primary">
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={700}
          color="text.primary"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {value} · {numFmt.format(percentage(value, total))}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, percentage(value, total))}
        aria-label={`${label}: ${value}`}
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 999 },
        }}
      />
    </Box>
  );
};

const CardBreakdown = ({ title, data, accent, matchesCount, t }) => {
  const numFmt = useNumberFormat();
  const sideTotal = data.amarillas + data.segundasAmarillas + data.rojas;
  return (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.default",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography component="h3" variant="subtitle1" fontWeight={800}>
          {title}
        </Typography>
        <Chip
          label={t("cards.total", { count: sideTotal })}
          size="small"
          sx={{ color: accent, fontVariantNumeric: "tabular-nums" }}
        />
      </Stack>
      <Stack spacing={1.25}>
        {[
          ["amarillas", t("cards.yellow")],
          ["segundasAmarillas", t("cards.secondYellow")],
          ["rojas", t("cards.red")],
        ].map(([field, label]) => (
          <Stack key={field} direction="row" justifyContent="space-between">
            <Typography variant="body2">{label}</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {data[field]}
              </Typography>
              {matchesCount > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                  aria-label={t("cards.perMatchAccessible", {
                    label, value: numFmt.format(average(data[field], matchesCount)),
                  })}
                >
                  {t("cards.perMatchValue", { value: numFmt.format(average(data[field], matchesCount)) })}
                </Typography>
              )}
            </Stack>
          </Stack>
        ))}
        <Divider />
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" fontWeight={700}>
            {t("cards.points")}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={800}
            color="primary.main"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {data.puntosDisciplinarios}
          </Typography>
        </Stack>
        {matchesCount > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t("cards.ratePerMatch")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {numFmt.format(average(sideTotal, matchesCount))}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const INTERVALS = [
  ["0_15", "0–15"],
  ["16_30", "16–30"],
  ["31_45", "31–45"],
  ["46_60", "46–60"],
  ["61_75", "61–75"],
  ["76_90_mas", "76+"],
];

const EstadisticasArbitroDetallePageModern = () => {
  const { t } = useTranslation("referees");
  const navigate = useNavigate();
  const location = useLocation();
  const { idArbitro } = useParams();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const competicionId = searchParams.get("idCompeticion") || "";
  const season = searchParams.get("idTemporada") || "";
  const [competiciones, setCompeticiones] = useState([]);
  const [retry, setRetry] = useState(0);
  const [detail, setDetail] = useState({ key: "", status: "loading", documents: [] });
  const headingRef = useRef(null);
  const requestKey = JSON.stringify([idArbitro, competicionId, season, retry]);
  const isCurrent = detail.key === requestKey;
  const loadingDetail = !isCurrent || detail.status === "loading";
  const detailError = isCurrent && detail.status === "error";
  const detailNotFound = isCurrent && detail.status === "notFound";
  const summary = useMemo(
    () => aggregateRefereeStatistics(isCurrent ? detail.documents : [], competiciones),
    [detail.documents, isCurrent, competiciones]
  );
  const profileCompetitions = useMemo(() => {
    const competitions = summary.competicionesDetalle.length
      ? summary.competicionesDetalle
      : competicionId ? [{ idCompeticion: competicionId, nombre: "" }] : [];
    return normalizeRefereeCompetitions(competitions, { deduplicateNames: true });
  }, [summary.competicionesDetalle, competicionId]);
  const numFmt = useNumberFormat();
  usePageTitle(summary.nombre ? `${summary.nombre} · ${t("profile")}` : t("profile"));

  useEffect(() => {
    let stale = false;
    getCompeticionesArbitros(navigate)
      .then((data) => {
        if (!stale) setCompeticiones(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (!idArbitro) {
      setDetail({ key: requestKey, status: "notFound", documents: [] });
      return;
    }
    let stale = false;
    setDetail({ key: requestKey, status: "loading", documents: [] });
    (season ? getEstadisticasArbitro(idArbitro, navigate, competicionId, season)
      : getEstadisticasArbitro(idArbitro, navigate, competicionId))
      .then((docs) => {
        if (stale) return;
        const documents = Array.isArray(docs) ? docs.filter(Boolean) : [];
        setDetail({
          key: requestKey,
          status: documents.length ? "success" : "notFound",
          documents,
        });
      })
      .catch((error) => {
        if (stale) return;
        setDetail({
          key: requestKey,
          status: Number(error?.status ?? error?.response?.status) === 404 ? "notFound" : "error",
          documents: [],
        });
      });
    return () => {
      stale = true;
    };
  }, [idArbitro, competicionId, season, navigate, requestKey]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [location.key, idArbitro, competicionId]);

  const rankingParams = new URLSearchParams(searchParams);
  rankingParams.delete("idArbitro");
  const rankingUrl = `/estadisticasArbitros${rankingParams.toString() ? `?${rankingParams}` : ""}`;
  const handleBack = () => {
    if (location.state?.refereeRankingUrl === rankingUrl) navigate(-1);
    else navigate(rankingUrl, { replace: true });
  };
  // --- Derived metrics ---
  const totalCards =
    summary.tarjetas.total.amarillas +
    summary.tarjetas.total.segundasAmarillas +
    summary.tarjetas.total.rojas;
  const dismissals =
    summary.tarjetas.total.segundasAmarillas + summary.tarjetas.total.rojas;
  const homeCards =
    summary.tarjetas.local.amarillas +
    summary.tarjetas.local.segundasAmarillas +
    summary.tarjetas.local.rojas;
  const awayCards =
    summary.tarjetas.visitante.amarillas +
    summary.tarjetas.visitante.segundasAmarillas +
    summary.tarjetas.visitante.rojas;
  const outcomeTotal =
    summary.partidos.victoriasLocal +
    summary.partidos.empates +
    summary.partidos.victoriasVisitante;
  const hasPenalties = summary.penaltisMarcados.total > 0;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, minWidth: 0 }}>
      <Stack spacing={3}>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ minHeight: 44, mb: 2 }}
          >
            {t("detail.clearSelection")}
          </Button>
          <Typography
            component="h1"
            variant="h4"
            fontWeight={900}
            ref={headingRef}
            tabIndex={-1}
            sx={{ overflowWrap: "anywhere" }}
          >
            {summary.nombre || t("profile")}
          </Typography>
        </Box>

        {/* Detail loading */}
        {loadingDetail && (
          <Box
            sx={{ minHeight: 180, display: "grid", placeItems: "center" }}
            role="status"
          >
            <Stack alignItems="center" spacing={1.5}>
              <CircularProgress aria-label={t("loading")} />
              <Typography color="text.secondary">{t("loading")}</Typography>
            </Stack>
          </Box>
        )}

        {/* Detail error */}
        {!loadingDetail && detailError && (
          <Stack spacing={1}>
            <Alert severity="error">{t("detail.error")}</Alert>
            <Box>
              <Button variant="outlined" onClick={() => setRetry((value) => value + 1)} sx={{ minHeight: 44 }}>
                {t("retry")}
              </Button>
            </Box>
          </Stack>
        )}

        {/* Not found state */}
        {!loadingDetail && detailNotFound && (
          <Card sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Stack spacing={1} alignItems="flex-start" role="status">
                <Typography fontWeight={700}>{t("detail.notFound")}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("detail.notFoundHint")}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Referee profile */}
        {!loadingDetail && isCurrent && detail.status === "success" && (
          <>
            {/* Profile header */}
            <Card
              sx={{
                color: "primary.contrastText",
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography component="h2" variant="h6" fontWeight={800}>
                      {t("profile")}
                    </Typography>
                    <Typography sx={{ opacity: 0.85 }}>
                      {t("sample", {
                        matches: summary.partidos.total,
                        seasons: summary.temporadas.length,
                      })}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                      alignItems="center"
                      sx={{ mt: 1 }}
                      role="group"
                      aria-label={t("profileCompetitions")}
                    >
                      {profileCompetitions.filter((competition) => competition.nombre).map((cd) => (
                        <Chip
                          key={cd.idCompeticion}
                          label={cd.nombre}
                          size="small"
                          sx={{
                            bgcolor: alpha("#fff", 0.2),
                            color: "inherit",
                          }}
                        />
                      ))}
                      {(profileCompetitions.length === 0 || profileCompetitions.some((competition) => !competition.nombre)) && (
                        <Typography component="span" variant="body2">
                          {t("competition.unknown")}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {summary.temporadas.map((season) => (
                      <Chip
                        key={season}
                        label={t("season", { season })}
                        sx={{ bgcolor: alpha("#fff", 0.16), color: "inherit" }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Metric cards — row 1 */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  icon={SportsSoccer}
                  label={t("metrics.matches")}
                  value={summary.partidos.total}
                  detail={t("metrics.sample")}
                  color={theme.palette.primary.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  icon={Style}
                  label={t("metrics.cardsPerMatch")}
                  value={numFmt.format(
                    average(totalCards, summary.partidos.total)
                  )}
                  detail={t("metrics.cardsTotal", { count: totalCards })}
                  color={theme.palette.warning.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  icon={Balance}
                  label={t("metrics.pointsPerMatch")}
                  value={numFmt.format(
                    average(
                      summary.tarjetas.total.puntosDisciplinarios,
                      summary.partidos.total
                    )
                  )}
                  detail={t("metrics.pointsTotal", {
                    count: summary.tarjetas.total.puntosDisciplinarios,
                  })}
                  color={theme.palette.error.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  icon={Scoreboard}
                  label={t("metrics.goalsPerMatch")}
                  value={numFmt.format(
                    average(summary.goles.total, summary.partidos.total)
                  )}
                  detail={t("metrics.goalsTotal", {
                    count: summary.goles.total,
                  })}
                  color={theme.palette.info.main}
                />
              </Grid>
            </Grid>

            {/* Metric cards — row 2 */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  icon={Style}
                  label={t("metrics.dismissalsPerMatch")}
                  value={numFmt.format(
                    average(dismissals, summary.partidos.total)
                  )}
                  detail={t("metrics.dismissalsTotal", {
                    count: dismissals,
                  })}
                  color={theme.palette.error.dark}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  icon={Balance}
                  label={t("metrics.homeShare")}
                  value={`${numFmt.format(percentage(homeCards, totalCards))}%`}
                  detail={`${homeCards} ${t("cards.home")}`}
                  color={theme.palette.success.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  icon={Balance}
                  label={t("metrics.awayShare")}
                  value={`${numFmt.format(percentage(awayCards, totalCards))}%`}
                  detail={`${awayCards} ${t("cards.away")}`}
                  color={theme.palette.info.dark}
                />
              </Grid>
              {hasPenalties && (
                <Grid item xs={12} sm={6} lg={3}>
                  <MetricCard
                    icon={SportsSoccer}
                    label={t("metrics.penaltiesPerMatch")}
                    value={numFmt.format(
                      average(
                        summary.penaltisMarcados.total,
                        summary.partidos.total
                      )
                    )}
                    detail={t("metrics.penaltiesTotal", {
                      count: summary.penaltisMarcados.total,
                    })}
                    color={theme.palette.secondary.main}
                  />
                </Grid>
              )}
            </Grid>

            {/* Cards breakdown + Result distribution */}
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Card
                  sx={{ height: "100%", border: `1px solid ${theme.palette.divider}` }}
                >
                  <CardContent>
                    <Typography component="h2" variant="h6" fontWeight={800} mb={0.5}>
                      {t("cards.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {t("cards.description")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <CardBreakdown
                          title={t("cards.home")}
                          data={summary.tarjetas.local}
                          accent={theme.palette.primary.main}
                          matchesCount={summary.partidos.total}
                          t={t}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CardBreakdown
                          title={t("cards.away")}
                          data={summary.tarjetas.visitante}
                          accent={theme.palette.info.main}
                          matchesCount={summary.partidos.total}
                          t={t}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card
                  sx={{ height: "100%", border: `1px solid ${theme.palette.divider}` }}
                >
                  <CardContent>
                    <Typography component="h2" variant="h6" fontWeight={800} mb={0.5}>
                      {t("results.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      {t("results.description")}
                    </Typography>
                    <Stack spacing={2.5}>
                      <DistributionRow
                        label={t("results.home")}
                        value={summary.partidos.victoriasLocal}
                        total={outcomeTotal}
                        color={theme.palette.success.main}
                      />
                      <DistributionRow
                        label={t("results.draw")}
                        value={summary.partidos.empates}
                        total={outcomeTotal}
                        color={theme.palette.warning.main}
                      />
                      <DistributionRow
                        label={t("results.away")}
                        value={summary.partidos.victoriasVisitante}
                        total={outcomeTotal}
                        color={theme.palette.info.main}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Timing + Score state */}
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Card
                  sx={{ height: "100%", border: `1px solid ${theme.palette.divider}` }}
                >
                  <CardContent>
                    <Typography component="h2" variant="h6" fontWeight={800} mb={0.5}>
                      {t("timing.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      {t("timing.description")}
                    </Typography>
                    <Stack spacing={2}>
                      {INTERVALS.map(([key, label]) => (
                        <DistributionRow
                          key={key}
                          label={`${label}'`}
                          value={summary.tarjetas.total.porIntervalo[key] || 0}
                          total={totalCards}
                          color={theme.palette.primary.main}
                        />
                      ))}
                      {summary.tarjetas.total.porIntervalo["desconocido"] > 0 && (
                        <DistributionRow
                          label={t("timing.unknown")}
                          value={summary.tarjetas.total.porIntervalo["desconocido"]}
                          total={totalCards}
                          color={theme.palette.text.secondary}
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card
                  sx={{ height: "100%", border: `1px solid ${theme.palette.divider}` }}
                >
                  <CardContent>
                    <Typography component="h2" variant="h6" fontWeight={800} mb={0.5}>
                      {t("scoreState.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      {t("scoreState.description")}
                    </Typography>
                    <Grid container spacing={2}>
                      {["Ganando", "Empatando", "Perdiendo"].map((state) => (
                        <Grid item xs={12} sm={4} key={state}>
                          <Box
                            sx={{
                              p: 2,
                              textAlign: "center",
                              border: 1,
                              borderColor: "divider",
                              borderRadius: 2,
                              bgcolor: "background.default",
                            }}
                          >
                            <Typography
                              component="p"
                              variant="h5"
                              fontWeight={900}
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {summary.tarjetas.total.porEstado[state] || 0}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {t(`scoreState.${state.toLowerCase()}`)}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    {hasPenalties && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Stack direction="row" justifyContent="space-between">
                          <Typography color="text.secondary">
                            {t("penalties")}
                          </Typography>
                          <Typography
                            fontWeight={800}
                            sx={{ fontVariantNumeric: "tabular-nums" }}
                          >
                            {summary.penaltisMarcados.total}
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Matches table */}
            <Card sx={{ border: `1px solid ${theme.palette.divider}`, minWidth: 0 }}>
              <CardContent sx={{ minWidth: 0 }}>
                <Typography component="h2" variant="h6" fontWeight={800} mb={0.5}>
                  {t("matches.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {t("matches.description")}
                </Typography>
                <Box sx={{ minWidth: 0 }}>
                  <TableContainer
                    tabIndex={0}
                    role="region"
                    aria-label={t("matches.title")}
                    sx={{ maxWidth: "100%", overflowX: "auto" }}
                  >
                    <Table
                      size="small"
                      aria-label={t("matches.title")}
                      sx={{
                        "& td, & th": { fontVariantNumeric: "tabular-nums" },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("matches.date")}</TableCell>
                          <TableCell>{t("matches.match")}</TableCell>
                          <TableCell>{t("matches.category")}</TableCell>
                          <TableCell>{t("competition.label")}</TableCell>
                          <TableCell align="center">
                            {t("matches.score")}
                          </TableCell>
                          <TableCell align="right">
                            {t("matches.yellow")}
                          </TableCell>
                          <TableCell align="right">
                            {t("matches.secondYellow")}
                          </TableCell>
                          <TableCell align="right">
                            {t("matches.red")}
                          </TableCell>
                          <TableCell align="right">
                            {t("matches.season")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.partidosDetalle.slice(0, 20).map((match, index) => (
                          <TableRow
                            key={`${match.codActa}-${match.idGrupo}-${match.idTemporada}-${index}`}
                            hover
                          >
                            <TableCell>{match.fecha || t("matches.unknownDate")}</TableCell>
                            <TableCell>
                              {refereeDisplayName(match.equipoLocal, match.idEquipoLocal) || t("matches.unknownHome")} –{" "}
                              {refereeDisplayName(match.equipoVisitante, match.idEquipoVisitante) || t("matches.unknownAway")}
                            </TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>
                              {refereeDisplayName(match.nombreCategoria, match.idCategoria) || t("matches.unknownCategory")}
                            </TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>
                              {refereeDisplayName(match.nombreCompeticion, match.idCompeticion) || t("competition.unknown")}
                            </TableCell>
                            <TableCell align="center">
                              {match.golesLocal != null && match.golesVisitante != null
                                ? `${match.golesLocal}–${match.golesVisitante}`
                                : t("matches.unknownScore")}
                            </TableCell>
                            <TableCell align="right">
                              {safeCount(match.tarjetasLocal?.amarillas) +
                                safeCount(match.tarjetasVisitante?.amarillas)}
                            </TableCell>
                            <TableCell align="right">
                              {safeCount(match.tarjetasLocal?.segundasAmarillas) +
                                safeCount(match.tarjetasVisitante?.segundasAmarillas)}
                            </TableCell>
                            <TableCell align="right">
                              {safeCount(match.tarjetasLocal?.rojas) +
                                safeCount(match.tarjetasVisitante?.rojas)}
                            </TableCell>
                            <TableCell align="right">
                              {match.idTemporada ?? t("matches.unknownSeason")}
                            </TableCell>
                          </TableRow>
                        ))}
                        {summary.partidosDetalle.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                py={2}
                              >
                                {t("matches.empty")}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Stack>
    </Container>
  );
};

export default EstadisticasArbitroDetallePageModern;